// TypeScript React
import React, { createContext, useContext, useMemo, useState } from 'react';
import { AiResearchContextType } from '../types/ai.types';

type EdisonDockContextType = {
    isOpen: boolean;
    setOpen: (v: boolean) => void;
    bottomOffset: number; // px
    rightOffset: number; // px
    setOffsets: (o?: Partial<{ bottom: number; right: number }>) => void;
    setHidden: (v: boolean) => void;
    isHidden: boolean;
    setShowResearchTab: (v: boolean) => void;
    showResearchTab: boolean;
    entityContext: AiResearchContextType | null;
    setEntityContext: (contextType: string, contextId: number) => void;
    clearEntityContext: () => void;
};

const EdisonDockContext = createContext<EdisonDockContextType | null>(null);

export function useEdisonDock() {
    const ctx = useContext(EdisonDockContext);
    if (!ctx) throw new Error('useChatDock must be used within ChatDockProvider');
    return ctx;
}

export function EdisonDockProvider({ children }: { children: React.ReactNode }) {
    const [isOpen, setOpen] = useState(false);
    const [isHidden, setHidden] = useState(false);
    const [bottomOffset, setBottomOffset] = useState(0);
    const [rightOffset, setRightOffset] = useState(0);
    const [showResearchTab, setShowResearchTabState] = useState(false);
    const [entityContext, setEntityContextState] = useState<AiResearchContextType | null>(null);

    const setOffsets = (o?: Partial<{ bottom: number; right: number }>) => {
        if (o) {
            if (o.bottom != null) setBottomOffset(o.bottom);
            if (o.right != null) setRightOffset(o.right);
        } else {
            //empty object means reset to default
            setBottomOffset(0);
            setRightOffset(0);
        }
    };

    const setShowResearchTab = (v: boolean): void => {
        setShowResearchTabState(v);
    };

    const setEntityContext = (contextType: string, contextId: number): void => {
        setEntityContextState({ contextType, contextId });
    };

    const clearEntityContext = (): void => {
        setEntityContextState(null);
    };

    const value = useMemo(
        () => ({
            isOpen,
            setOpen,
            bottomOffset,
            rightOffset,
            setOffsets,
            setHidden: (hidden: boolean) => {
                setHidden(hidden);
            },
            isHidden,
            showResearchTab,
            setShowResearchTab,
            entityContext,
            setEntityContext,
            clearEntityContext,
        }),
        [isOpen, bottomOffset, rightOffset, isHidden, showResearchTab, entityContext]
    );

    return <EdisonDockContext.Provider value={value}>{children}</EdisonDockContext.Provider>;
}
